/**
 * SOLID Review
 * S — Strong. One concern: the data export (GDPR Art. 20) — ask for the player's data, hand it to them as a JSON file, or say why not.
 * O — Good. Another format would be another download of the same answer.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. One action.
 * D — Strong. One use case, the request contexts and the platform's downloads.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import type { IServiceViewDownloads } from './platform/IConfigDepViewPlatform';
import type { ClientRequests } from '../client/ClientRequests';
import { messageFor } from '../client/failureMessage';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';

const EXPORT: TClientUseCaseInput<'exportMyData'> = {};
/** The file the player gets. */
const EXPORT_FILE = 'meyerlive3d-my-data.json';

export interface IStateGdprExport {
  readonly exporting: boolean;
  readonly exportError: string | null;
}

const IDLE: IStateGdprExport = { exporting: false, exportError: null };
const EXPORTING: Partial<IStateGdprExport> = { exporting: true, exportError: null };
const EXPORTED: Partial<IStateGdprExport> = { exporting: false };

/**
 * THE DATA EXPORT (frontend-framework-oo P3, 2026-10-29, §28.295) — `legal/components/tabs/GdprExportSection.tsx`'s state and handler;
 * the browser download it made itself (a Blob, an object URL, an anchor clicked) is the platform's now.
 */
export class GdprExportViewModel implements IServiceViewModel<IStateGdprExport> {
  private readonly state = new ObservableState<IStateGdprExport>(IDLE);

  constructor(
    private readonly useCases: Pick<IPortClientUseCases, 'exportMyData'>,
    private readonly requests: ClientRequests,
    private readonly downloads: IServiceViewDownloads,
  ) {}

  readonly export = async (): Promise<void> => {
    this.state.update(EXPORTING);
    try {
      const exported = await this.useCases.exportMyData(this.requests.context(), EXPORT);
      if (!exported.success) {
        const failed: Partial<IStateGdprExport> = { exportError: messageFor(exported.error, 'Export failed.') };
        this.state.update(failed);
        return;
      }
      this.downloads.save(EXPORT_FILE, JSON.stringify(exported.value.data, null, 2), 'application/json');
    } finally {
      this.state.update(EXPORTED);
    }
  };

  getSnapshot(): IStateGdprExport {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {}
}
